import { Bell, Mail, MailOpen } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useNavBadges } from '@/components/layout/use-nav-badges';
import { AttentionBadge } from '@/components/ui/attention-badge';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  type NotificationItem,
  useClearNotificationsMutation,
  useMarkNotificationUnreadMutation,
  useMarkNotificationsReadMutation,
  useNotificationsFeedQuery,
  useUnreadNotificationsCountQuery,
} from '@/services';

import { iconForCategory } from './notification-icons';

const UNREAD_DOT: Partial<Record<NotificationItem['urgency'], string>> = {
  urgent: 'bg-urgent',
  action: 'bg-action',
};

/**
 * The AppBar's inbox: everything waiting somewhere in the nav (`useNavBadges`), plus the staff
 * member's own notification feed below it. The bell's badge is both added together, so open
 * tasks show without opening the panel; only a rise in unread notifications pulses it.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { entries, total: pendingTotal } = useNavBadges();
  const pending = entries.filter(e => e.count > 0);

  const { data: unread } = useUnreadNotificationsCountQuery();
  const feed = useNotificationsFeedQuery();
  const { mutate: markRead } = useMarkNotificationsReadMutation();
  const { mutate: markUnread } = useMarkNotificationUnreadMutation();
  const { mutate: clearAll } = useClearNotificationsMutation();

  // Pulses only on a rise from a previously-known count — never on the first load, where
  // there's nothing to compare against yet.
  const [pulse, setPulse] = useState(false);
  const prevUnreadRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    const count = unread?.count;
    if (count === undefined) return;
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = count;
    if (prev === undefined || count <= prev) return;

    setPulse(true);
    const timer = setTimeout(() => setPulse(false), 1000);
    return () => clearTimeout(timer);
  }, [unread?.count]);

  const items = feed.data?.pages.flatMap(page => page.items) ?? [];
  const hasAnything = pending.length > 0 || items.length > 0;

  function openItem(item: NotificationItem) {
    markRead({ ids: [item.id] });
    navigate(item.href);
  }

  async function onClearAll() {
    if (await confirm({ title: t('notifications.clear_all_confirm'), variant: 'destructive' })) clearAll();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-7 w-7"
          aria-label={t('notifications.title')}
          title={t('notifications.title')}
        >
          <Bell className="h-3.5 w-3.5" />
          <AttentionBadge
            count={(unread?.count ?? 0) + pendingTotal}
            label={t('notifications.title')}
            className={cn('absolute -right-0.5 -top-0.5 h-4 min-w-4 px-1 text-[10px]', pulse && 'notif-anim-pulse')}
          />
        </Button>
      </DropdownMenuTrigger>
      {/* Capped to the room under the bell: a long feed scrolls inside, so the actions below it
          stay on screen instead of being pushed past the viewport and clipped. */}
      <DropdownMenuContent className="flex max-h-[min(40rem,var(--radix-dropdown-menu-content-available-height))] w-80 flex-col">
        {!hasAnything ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('notifications.empty')}</p>
        ) : (
          <>
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1">
              {/* Two different things share this panel: work waiting in the sidebar, and the
                reader's own inbox. Unlabelled, a full pending list over an empty inbox reads as
                three mystery counts — so each group says what it is. */}
              {pending.length > 0 && (
                <>
                  <p className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('notifications.pending_items')}
                  </p>
                  {pending.map(e => (
                    <DropdownMenuItem key={e.href} asChild>
                      <Link to={e.to ?? e.href} className="flex items-center justify-between gap-3">
                        <span className="truncate">{t(e.labelKey)}</span>
                        <AttentionBadge count={e.count} />
                      </Link>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <p className="px-2 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('notifications.title')}
                  </p>
                  {items.length === 0 && (
                    <p className="px-2 pb-1.5 text-sm text-muted-foreground">{t('notifications.empty')}</p>
                  )}
                </>
              )}
              {items.map(item => {
                const Icon = iconForCategory(item.category);
                const isRead = item.readAt !== null;
                return (
                  <DropdownMenuItem
                    key={item.id}
                    className="items-start gap-2"
                    onClick={() => openItem(item)}
                    // Radix's roving focus lands here first, and its content-level handler
                    // swallows every Tab that reaches it unblocked — stop it from bubbling past
                    // the row so a plain Tab can still hand focus to the toggle button below.
                    onKeyDown={e => {
                      if (e.key === 'Tab') e.stopPropagation();
                    }}
                  >
                    {!isRead && (
                      <span
                        aria-hidden
                        className={cn(
                          'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                          UNREAD_DOT[item.urgency] ?? 'bg-primary'
                        )}
                      />
                    )}
                    <Icon
                      aria-hidden
                      data-testid={`notification-icon-${item.id}`}
                      data-category={item.category}
                      className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{t(`notifications.item.${item.category}.${item.type}`, item.params)}</p>
                      <p className="text-xs text-muted-foreground">{formatRelativeTime(item.createdAt)}</p>
                    </div>
                    <button
                      type="button"
                      aria-label={isRead ? t('notifications.mark_unread') : t('notifications.mark_read')}
                      onClick={e => {
                        // The row itself opens the record, so this stops the click before it gets there.
                        e.stopPropagation();
                        if (isRead) markUnread(item.id);
                        else markRead({ ids: [item.id] });
                      }}
                      onKeyDown={e => e.stopPropagation()}
                      className={cn(
                        'shrink-0 rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                      )}
                    >
                      {isRead ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                    </button>
                  </DropdownMenuItem>
                );
              })}
              {feed.hasNextPage && (
                <DropdownMenuItem
                  onSelect={e => {
                    e.preventDefault();
                    feed.fetchNextPage();
                  }}
                >
                  {t('notifications.load_more')}
                </DropdownMenuItem>
              )}
            </div>
            {items.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="flex shrink-0 items-center justify-between gap-2 px-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => markRead({ all: true })}
                  >
                    {t('notifications.mark_all_read')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={onClearAll}
                  >
                    {t('notifications.clear_all')}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
